package top.wcpe.allincore.ui.web.asset

import kotlin.js.Promise

internal open class AssetPolicyException(message: String) : IllegalArgumentException(message)

internal enum class AssetSourceFailure {
    NOT_FOUND,
    READ_FAILED,
}

internal class AssetSourceException(
    val failure: AssetSourceFailure,
    message: String,
) : AssetPolicyException(message)

internal enum class AssetSource {
    URL,
    PAGES,
    FILE_SYSTEM,
    JCEF,
}

internal enum class AssetBudgetClass {
    TEXT,
    AICR,
    BBMODEL,
    IMAGE,
}

internal enum class AssetReferenceKind {
    SOURCE_IMPORT,
    THEME,
    MOCK,
    MODEL,
    TEXTURE,
    BACKGROUND,
}

internal data class AssetContentRequest(
    val canonicalPath: String,
    val source: AssetSource,
    val responseMime: String?,
    val expectedMime: String?,
    val bytes: ByteArray,
)

internal data class AssetContentDescriptor(val mime: String, val budgetClass: AssetBudgetClass, val actualBytes: Long)

internal data class AssetSourceRead(
    val bytes: ByteArray,
    val declaredMime: String?,
    val versionMetadata: String?,
    val declaredBytes: Long?,
)

internal class AssetSourceReadOperation(val result: Promise<AssetSourceRead>, private val cancelAction: () -> Unit) {
    private var cancelled = false

    fun cancel() {
        if (cancelled) return
        cancelled = true
        cancelAction()
    }
}

internal sealed interface AssetReadLimit {
    val maximumBytes: Long

    fun byteLimitFor(prefix: ByteArray, complete: Boolean = true): Long

    data class Fixed(override val maximumBytes: Long) : AssetReadLimit {
        init {
            require(maximumBytes > 0L) { "资产读取上限必须大于零" }
        }

        override fun byteLimitFor(prefix: ByteArray, complete: Boolean): Long = maximumBytes
    }

    data object AicrOrText : AssetReadLimit {
        override val maximumBytes: Long = AssetBudgetLimits.BINARY_BYTES

        override fun byteLimitFor(prefix: ByteArray, complete: Boolean): Long = when {
            prefix.size >= AICR_MAGIC_BYTES && hasAicrMagic(prefix) -> maximumBytes
            prefix.size >= AICR_MAGIC_BYTES || complete -> AssetBudgetLimits.TEXT_BYTES
            else -> maximumBytes
        }
    }
}

internal interface AssetSourceAdapter {
    val source: AssetSource
    fun read(canonicalPath: String, readLimit: AssetReadLimit): AssetSourceReadOperation
    fun dispose()
}

internal interface AssetObjectUrlBackend {
    fun create(bytes: ByteArray, mime: String): String
    fun revoke(url: String)
}

internal class AssetJpegCheckOperation(val result: Promise<Boolean>, private val cancelAction: () -> Unit) {
    private var cancelled = false

    fun cancel() {
        if (cancelled) return
        cancelled = true
        cancelAction()
    }
}

internal fun interface AssetJpegIntegrityChecker {
    fun check(bytes: ByteArray): AssetJpegCheckOperation
}

internal data class AssetFetchResponse(
    val finalUrl: String,
    val status: Int,
    val declaredMime: String?,
    val contentLength: Long?,
    val etag: String?,
    val lastModified: String?,
    val bytes: ByteArray,
)

internal class AssetFetchOperation(val result: Promise<AssetFetchResponse>, private val cancelAction: () -> Unit) {
    private var cancelled = false

    fun cancel() {
        if (cancelled) return
        cancelled = true
        cancelAction()
    }
}

internal fun interface AssetFetchBackend {
    fun fetch(url: String, readLimit: AssetReadLimit): AssetFetchOperation
}

internal data class AssetUri(
    val value: String,
    val origin: String,
    val pathname: String,
    val search: String = "",
    val hash: String = "",
    val protocol: String = "",
)

internal interface AssetUriBackend {
    fun resolve(baseUri: String, canonicalPath: String): AssetUri
    fun parse(uri: String): AssetUri
}

internal interface AssetFileSystemSnapshot {
    val prefix: ByteArray
    val declaredMime: String?
    val size: Long
    val lastModified: Long

    fun read(maxBytes: Long): Promise<ByteArray>
}

internal interface AssetFileSystemDirectoryHandle {
    fun getDirectoryHandle(name: String): Promise<AssetFileSystemDirectoryHandle>
    fun getFileHandle(name: String): Promise<AssetFileSystemFileHandle>
}

internal fun interface AssetFileSystemFileHandle {
    fun openSnapshot(prefixBytes: Int): Promise<AssetFileSystemSnapshot>
}

internal enum class AssetFileSystemFailure {
    NOT_FOUND,
    PERMISSION_REVOKED,
    CANCELLED,
    READ_FAILED,
}

internal class AssetFileSystemException(val failure: AssetFileSystemFailure) : RuntimeException()

internal fun interface JpegDecodeChecker {
    fun isComplete(bytes: ByteArray): Boolean
}
